import React, {useContext, useCallback} from "react";
import Headroom from "react-headroom";
import "./Header.scss";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import StyleContext from "../../contexts/StyleContext";
import {
  workExperiences,
  skillsSection,
  achievementSection,
  resumeSection
} from "../../portfolio";
import {useTranslation} from "react-i18next";
import FlagFR from "../../assets/flags/fr.svg";
import FlagGB from "../../assets/flags/gb.svg";

function Header() {
  const {isDark} = useContext(StyleContext);
  const {t, i18n} = useTranslation();

  const viewExperience = workExperiences.display;
  const viewSkills = skillsSection.display;
  const viewAchievement = achievementSection.display;
  const viewResume = resumeSection.display;

  /** ✅ Helper to safely send GA events */
  const trackEvent = useCallback((action, category, label) => {
    if (window.gtag) {
      window.gtag("event", action, {
        event_category: category,
        event_label: label
      });
    }
  }, []);

  /** ✅ Track menu click */
  const trackMenuClick = useCallback(
    label => trackEvent("menu_click", "navigation", label),
    [trackEvent]
  );

  /** ✅ Change language + track event */
  const handleLanguageChange = useCallback(() => {
    const newLang = i18n.language.startsWith("fr") ? "en" : "fr";
    i18n.changeLanguage(newLang);

    // Track language change in Google Analytics
    trackEvent("language_change", "i18n", newLang);
  }, [i18n, trackEvent]);

  /** ✅ Determine flag + tooltip */
  const isFrench = i18n.language.startsWith("fr");
  const flagSrc = isFrench ? FlagGB : FlagFR;
  const flagAlt = isFrench
    ? t("language.switchToEnglish")
    : t("language.switchToFrench");
  const flagTitle = flagAlt;

  return (
    <Headroom>
      <header className={isDark ? "dark-menu header" : "header"}>
        <a href="/" className="logo" onClick={() => trackMenuClick("Home")}>
          <span className="grey-color">&lt;</span>
          <span className="logo-name">{t("greeting.username")}</span>
          <span className="grey-color">/&gt;</span>
        </a>

        <input className="menu-btn" type="checkbox" id="menu-btn" />
        <label
          className="menu-icon"
          htmlFor="menu-btn"
          style={{color: "white"}}
        >
          <span className={isDark ? "navicon navicon-dark" : "navicon"}></span>
        </label>

        <ul className={isDark ? "dark-menu menu" : "menu"}>
          {viewSkills && (
            <li>
              <a href="#skills" onClick={() => trackMenuClick("Skills")}>
                {t("menu.skills", "Skills")}
              </a>
            </li>
          )}
          {viewExperience && (
            <li>
              <a
                href="#experience"
                onClick={() => trackMenuClick("Work Experiences")}
              >
                {t("menu.experience", "Work Experiences")}
              </a>
            </li>
          )}
          {viewAchievement && (
            <li>
              <a
                href="#certifications"
                onClick={() => trackMenuClick("Certifications")}
              >
                {t("menu.certifications", "Certifications")}
              </a>
            </li>
          )}
          {viewResume && (
            <li>
              <a href="#resume" onClick={() => trackMenuClick("Resume")}>
                {t("menu.resume", "Resume")}
              </a>
            </li>
          )}
          <li>
            <a href="#contact" onClick={() => trackMenuClick("Contact Me")}>
              {t("menu.contact", "Contact Me")}
            </a>
          </li>

          {/* 🌍 Language Switch */}
          <li>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a onClick={handleLanguageChange} title={flagTitle}>
              <img src={flagSrc} alt={flagAlt} className="flag-icon" />
            </a>
          </li>

          {/* 🌙 Theme Toggle */}
          <li>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a onClick={() => trackMenuClick("Theme Toggle")}>
              <ToggleSwitch />
            </a>
          </li>
        </ul>
      </header>
    </Headroom>
  );
}

export default Header;
